import { useState, useCallback, useEffect, useContext } from 'react';
import { useAlert } from '../functions/useAlert';
import { DataContext } from '../contexts/DataProvider';
import { fetchAgreements, deleteAgreement, markAgreementReady } from '../services/agreements.service';

export interface Agreement {
  _id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  createdAt: string;
  uploadedAt?: string;
  uploadedBy: string;
  blobUrl: string;
  orgId: string;
  docusealTemplateId?: number;
  docusealSlug?: string;
  isTemplateReady?: boolean;
}

interface AgreementsState {
  agreements: Agreement[];
  isLoading: boolean;
  selectedAgreement: Agreement | null;
  setSelectedAgreement: (a: Agreement | null) => void;
  deleteTarget: Agreement | null;
  setDeleteTarget: (a: Agreement | null) => void;
  configureTarget: Agreement | null;
  setConfigureTarget: (a: Agreement | null) => void;
  isMarkingReady: boolean;
  refetch: () => void;
  handleDelete: () => Promise<void>;
  handleMarkReady: () => Promise<void>;
  handleDownload: (agreement: Agreement) => Promise<void>;
}

export const useAgreements = (selectedOrganization?: string): AgreementsState => {
  const [setAlert] = useAlert();
  const { login }: any = useContext(DataContext);

  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState<Agreement | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Agreement | null>(null);
  const [configureTarget, setConfigureTarget] = useState<Agreement | null>(null);
  const [isMarkingReady, setIsMarkingReady] = useState(false);

  const refetch = useCallback(async () => {
    if (!selectedOrganization) { setAgreements([]); return; }
    setIsLoading(true);
    try {
      const orgId = selectedOrganization === 'All Organisation' ? login.data.orgId : selectedOrganization;
      const resp: any = await fetchAgreements(orgId);
      if (resp?.hasError) throw new Error(resp.message);
      setAgreements(Array.isArray(resp?.data) ? resp.data : []);
    } catch (err: any) {
      setAlert(err.message || 'Failed to load agreements.', 'error');
      setAgreements([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedOrganization, login.data.orgId, setAlert]);

  useEffect(() => {
    setSelectedAgreement(null);
    refetch();
  }, [selectedOrganization]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const resp: any = await deleteAgreement(deleteTarget._id);
      if (resp?.hasError) throw new Error(resp.message);
      setAlert('Agreement deleted.', 'success');
      if (selectedAgreement?._id === deleteTarget._id) setSelectedAgreement(null);
      setDeleteTarget(null);
      refetch();
    } catch (err: any) {
      setAlert(err.message || 'Failed to delete.', 'error');
    }
  };

  const handleMarkReady = async () => {
    if (!configureTarget) return;
    setIsMarkingReady(true);
    try {
      const resp: any = await markAgreementReady(configureTarget._id);
      if (resp?.hasError) throw new Error(resp.message);
      setAlert('Template saved and marked as ready!', 'success');
      setConfigureTarget(null);
      refetch();
    } catch (err: any) {
      setAlert(err.message || 'Failed to mark template as ready.', 'error');
    } finally {
      setIsMarkingReady(false);
    }
  };

  const handleDownload = async (agreement: Agreement) => {
    try {
      const resp = await fetch(agreement.blobUrl);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = agreement.fileName; a.click();
      URL.revokeObjectURL(url);
    } catch {
      setAlert('Failed to download file.', 'error');
    }
  };

  return {
    agreements, isLoading,
    selectedAgreement, setSelectedAgreement,
    deleteTarget, setDeleteTarget,
    configureTarget, setConfigureTarget,
    isMarkingReady, refetch,
    handleDelete, handleMarkReady, handleDownload,
  };
};
